import * as React from "react";
import * as ReactDOM from "react-dom";
import { Route } from "react-router-dom";
import { IComboBoxValue } from "../../../models/form";
import * as models from "../../../models";
import * as modelsGet from "../../../models/get";
import * as modelsPost from "../../../models/post";
import * as utils from "../../../utils/utils";
import * as apiTemplates from "../../../api/templates";
import { Window } from "../../../components/window";
import { Loader, LoaderStyle } from "../../../components/loader";
import { Button, ButtonType } from "../../../components/button";
import { ScrollViewer, ScrollBarVisibility } from "../../../components/scrollViewer";
import { FormGroup } from "../../../components/form";
import { ComboBox, ComboBoxType, DROPDOWN_TEXT_ITEM_HEIGHT, DropDownTextItem } from "../../../components/comboBox";
import { InputMask, InputSize } from "../../../components/input";
import { InputValueType } from "../../../enumerations";
import { TextBox } from "../../../components/textBox";
import { TextArea } from "../../../components/textArea";
import { Alert, AlertType } from "../../../components/alert";
import { CheckBox } from "../../../components/checkbox";
import ColorPicker from "../../../components/colorpicker";
import { LABEL_COLORS, LABEL_PATTERNS } from "../../../utils/constants";

enum Mode
{
    create,
    edit
}

interface IProperties
{
    /**
     * Die ID des zu öffnenden Datensatzes. (Ist leer wenn eine neuer Datensatz erstellt werden soll.)
     */
    reportId?: number,

    /**
     * Wird aufgerufen sobald sich das Fenster schließt.
     */
    onClose?: (refresh?: boolean) => void
}

interface IState
{
    /**
     * Gibt an, ob Daten gerade geladen werden.
     */
    isLoading?: boolean,

    /**
     * Gibt den Modus an in dem sich der Dialog befindet.
     */
    mode?: Mode,

    /**
     * Wenn im Bearbeitungsmodus, dann ist diese Eigenschaft gefüllt.
     */
    report?: modelsGet.ITemplate,

    /**
     * Enthält die Eingabedaten des Forms.
     */
    form?: {
        name?: string,
        display_name?: string,
        is_list?: boolean,
        custom_text_1?: string,
        custom_text_2?: string
    },

    /**
     * Enthält Serverseitige Fehlermeldungen.
     */
    errors?: Object,

    /**
     * Der Text der Meldungen welche angezeigt werden soll.
     */
    alertMessage?: string,

    /**
     * Der Typ der Meldung.
     */
    alertType?: AlertType
}

export default class Dialog extends React.Component<IProperties, IState>
{
    private _initialState: IState;
    private _isSaveButtonDisabled: boolean;

    /**
     * Erstellt eine neue Instanz der Klasse @see Dialog.
     * @param props Die Eigenschaften dieser Komponente.
     */
    constructor(props: IProperties)
    {
        super(props);

        // Modus ermitteln.
        var mode: Mode = Mode.create;
        if (props.reportId > 0)
        {
            mode = Mode.edit;
        }

        this.state = {
            isLoading: true,
            mode: mode,
            form: {},
            errors: {}
        };

        this.initAsync(props);
    }

    /**
     * Lädt alle benötigten Daten und/oder weißt diese zu.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync(props: IProperties)
    {
        if (this.state.mode === Mode.edit)
        {
            await this.initEditModeAsync(props);
        }
        else if (this.state.mode === Mode.create)
        {
            await this.initCreateModeAsync(props);
        }
    }

    /**
     * Initialisert den Edit Mode.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initEditModeAsync(props: IProperties)
    {
        // Betreff aus der Datenbank laden.
        var report: modelsGet.ITemplate = await apiTemplates.getTemplate(props.reportId);

        // Werte setzen.
        this.setState((state: IState) =>
        {
            state.isLoading = false;
            state.errors = {};
            state.alertMessage = null;
            state.alertType = null;
            state.report = report;

            state.form.name = report.name;
            state.form.display_name = report.display_name;
            state.form.is_list = report.is_list;
            state.form.custom_text_1 = report.custom_text_1;
            state.form.custom_text_2 = report.custom_text_2;

            return state;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }

    /**
     * Initialisert den Create Mode.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initCreateModeAsync(props: IProperties)
    {
        // Irgend etwas muss geladen werden, sonst funktioniert setState nicht.
        var dummy = await apiTemplates.getTemplate(0);

        // Neues State Objekt erstellen.
        this.setState(() =>
        {
            const newState: IState = {};
            newState.isLoading = false;
            newState.errors = {};
            newState.alertMessage = null;
            newState.alertType = null;

            newState.form = {};

            return newState;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }

    /**
     * Wird ausgeführt, bevor gerendert wird.
     * Hier wird geprüft ob der Button zum erstellen oder speichern
     * freigschaltet werden soll.
     * @param nextProps Die neuen Eigenschaften.
     * @param nextState Der neue State.
     */
    componentWillUpdate(nextProps: IProperties, nextState: IState)
    {
        this._isSaveButtonDisabled = true;

        if (this._initialState != null)
        {
            const { form } = this._initialState;

            if (form.name !== nextState.form.name)
                this._isSaveButtonDisabled = false;

            if (form.display_name !== nextState.form.display_name)
                this._isSaveButtonDisabled = false;

            if (form.is_list !== nextState.form.is_list)
                this._isSaveButtonDisabled = false;

            if (form.custom_text_1 !== nextState.form.custom_text_1)
                this._isSaveButtonDisabled = false;

            if (form.custom_text_2 !== nextState.form.custom_text_2)
                this._isSaveButtonDisabled = false;
        }
    }

    /**
     * Wird ausgeführt, bevor der Dialog sich schließt.
     */
    private onClosing = (refresh: boolean = false) =>
    {
        var closeDialog: boolean = true;

        if (!this._isSaveButtonDisabled)
            closeDialog = confirm("Es sind noch ungespeicherte Änderungen vorhanden. Wollen Sie den Dialog trotzdem schließen?");

        if (closeDialog)
        {
            const { onClose } = this.props;
            onClose && onClose(refresh);
            return true;
        }
    }

    /**
     * Ändert den geöffneten Datensatz oder erstellt einen neuen.
     */
    private onSaveOrCreateClick = async () =>
    {
        try
        {
            this.setState({
                isLoading: true
            });

            const { mode, report, form } = this.state;

            // Neues POST Objekt erstellen.
            var newData: modelsPost.ITemplate = {};
            newData.name = form.name;
            newData.display_name = form.display_name;
            newData.is_list = form.is_list;
            newData.custom_text_1 = form.custom_text_1;
            newData.custom_text_2 = form.custom_text_2;

            if (mode === Mode.edit)
            {
                await apiTemplates.put(report.id, newData);
            }
            else
            {
                await apiTemplates.post(newData);
            }

            // Fenster schließen.
            this._isSaveButtonDisabled = true;
            this.onClosing(true);
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    /**
     * Löscht den geöffneten Datensatz.
     */
    private onDeleteClick = async () =>
    {
        var deleteData = confirm("Wollen Sie den Datensatz wirklich löschen?");
        if (!deleteData)
            return;

        try
        {
            this.setState({
                isLoading: true
            });

            const { report } = this.state;

            await apiTemplates.remove(report.id);

            // Fenster schließen.
            this._isSaveButtonDisabled = true;
            this.onClosing(true);
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    /**
     * Wird ausgeführt, sobald sich der Wert eines Controls ändert.
     * @param value Der konvertierte Wert der TextBox.
     * @param originalValue Der originale Wert der TextBox.
     * @param name Der Name des Form Elements.
     */
    private onControlTextChanged = (value: any[], originalValue: string[], name: string) =>
    {
        this.setState((state: IState) =>
        {
            state.form[name] = value[0];
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald sich der Wert einer CheckBox ändert.
     * @param e Das auslösende Ereignis.
     */
    private onCheckboxChanged = (e: React.ChangeEvent<HTMLInputElement>) =>
    {
        var target = e.target;
        var value = target.checked;

        this.setState((state: IState) =>
        {
            state.form.is_list = value;
            return state;
        });
    }

    /**
     * Rendert das Formular/den Content Bereich.
     */
    private renderContent(): JSX.Element
    {
        const {
            mode,
            report,
            form,
            errors,
            alertMessage,
            alertType
        } = this.state;

        return (
            <div className="settings_dialog_content">
                <ScrollViewer
                    HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                    VerticalScrollBarVisibility={ScrollBarVisibility.auto}
                    style={{
                        padding: 10
                    }}>
                    <FormGroup>
                        <TextBox
                            label="Anzeigename"
                            value={form.display_name}
                            size={InputSize.normal}
                            spellCheck={true}
                            autoFocus={true}
                            errorMessage={errors["DisplayName"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "display_name")} />
                        <TextBox
                            label="Dateiname"
                            value={form.name}
                            size={InputSize.normal}
                            errorMessage={errors["Name"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "name")} />
                        <TextArea
                            label="Textblock #1"
                            value={form.custom_text_1}
                            spellCheck={true}
                            errorMessage={errors["CustomText1"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "custom_text_1")} />
                        <TextArea
                            label="Textblock #2"
                            value={form.custom_text_2}
                            spellCheck={true}
                            errorMessage={errors["CustomText2"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "custom_text_2")} />
                        <CheckBox
                            style={{
                                marginTop: 10
                            }}
                            label="Report ist eine Liste"
                            isChecked={form.is_list}
                            onCheckedChanged={this.onCheckboxChanged} />
                    </FormGroup>
                </ScrollViewer>
            </div>
        );
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const {
            isLoading,
            mode,
            report,
            form,
            errors,
            alertMessage,
            alertType
        } = this.state;

        var icon: string = "loading";
        var title: string = "Daten werden geladen...";
        var buttonHeader: string = "";

        if (!isLoading)
        {
            if (mode === Mode.create)
            {
                icon = "plus";
                title = "Neuen Report erstellen";
                buttonHeader = "Erstellen";
            }
            else
            {
                icon = "pencil";
                title = `Report "${report.display_name}" bearbeiten`;
                buttonHeader = "Speichern";
            }
        }

        return (
            <Window
                isOpen={true}
                titleIcon={icon}
                title={title}
                onClosing={this.onClosing}
                isMaximized={false}
                width={600}
                height={560}
                onEnterKeyUp={this.onSaveOrCreateClick}
                onEscapeKeyUp={() => this.onClosing(false)}>
                <div className="settings_dialog">
                    <Alert
                        style={utils.getGridChildStyle(1, 1, 1, 1)}
                        alertType={alertType}
                        isVisible={alertMessage ? true : false}
                        isClosable={true}
                        onHide={() => this.setState({ alertMessage: null, alertType: null })}>
                        {alertMessage}
                    </Alert>
                    {isLoading ? <Loader loaderStyle={LoaderStyle.style1} /> : this.renderContent()}
                    <div className="settings_dialog_actions">
                        {
                            mode === Mode.edit &&
                            <Button
                                text="Löschen"
                                type={ButtonType.danger}
                                onClick={this.onDeleteClick} />
                        }
                        <div style={{ flexGrow: 1 }}></div>
                        <Button
                            text={buttonHeader}
                            type={ButtonType.primary}
                            isDisabled={this._isSaveButtonDisabled}
                            onClick={this.onSaveOrCreateClick} />
                        <Button
                            text="Abbrechen"
                            type={ButtonType.default}
                            onClick={() => this.onClosing(false)} />
                    </div>
                </div>
            </Window>
        );
    }
}